import { CountriesList } from '@/shared/constants/countries-list';
import { ProfileType } from '@/shared/generated/sdk';
import { FieldActionType } from '@/shared/lib/hooks/useFormRenderV2/constants';
import { BeneficiaryGroup, CollectionName } from '@/shared/lib/sj-orm';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { GenderList } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import {
  IdStatusList,
  LegalizationType,
  StorageLocationList,
  StorageLocationType,
} from '@/shared/lib/sj-orm/models/personalIdentifiersDocuments';
import {
  PersonalIdentifierDetails,
  PersonalIdentifierGeneral,
} from '@/shared/lib/sj-orm/models/personalIdentifiersDocuments/personalIdentifiersDocuments.dto';

import { TDtoWrapper } from '../form-wrapper.types';

export type PersonalIdentifierGeneralHelp = {
  name: QuestionType.TEXT;
  beneficiaryId: QuestionType.PICK_ONE_OF_DTOS;
  country: QuestionType.SELECT_FROM_LIST;
  names: QuestionType.TEXT;
  surnames: QuestionType.TEXT;
  secondName: QuestionType.TEXT;
  attachments: QuestionType.FILE_UPLOAD;
};

export type PersonalIdentifierGeneralFormType = TDtoWrapper<
  Omit<PersonalIdentifierGeneral, 'createdAt' | 'updatedAt'>,
  PersonalIdentifierGeneralHelp
>;

export const commonGeneralFields: TDtoWrapper<
  PersonalIdentifierGeneral,
  PersonalIdentifierGeneralHelp
> = {
  name: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.title.label',
    required: true,
    colSpan: 2,
    tooltip: 'forms_personal_ids:common.inputs.title.tooltip',
    order: 1,
  },
  beneficiaryId: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_personal_ids:common.inputs.associatedWith.label',
    required: true,
    disableIfExists: true,
    order: 2,
    colName: CollectionName.BENEFICIARIES,
    dtoType: [BeneficiaryGroup.FAMILY_CLAN, BeneficiaryGroup.FAMILY, BeneficiaryGroup.CIRCLE],
    buttonAfter: {
      label: 'forms_personal_ids:common.inputs.associatedWith.button',
      action: FieldActionType.ASSIGN_TO_MYSELF,
    },
    hideCondition: {
      allowedProfileTypes: [ProfileType.MainUser, ProfileType.Beneficiary],
    },
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:common.inputs.country.label',
    items: CountriesList,
    withSearch: true,
    required: true,
    order: 3,
  },
  names: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.firstName.label',
    placeholder: 'forms_personal_ids:common.inputs.firstName.placeholder',
    required: true,
    capitalized: true,
    order: 4,
  },
  surnames: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.surname.label',
    placeholder: 'forms_personal_ids:common.inputs.surname.placeholder',
    required: true,
    capitalized: true,
    order: 5,
  },
  secondName: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.secondName.label',
    capitalized: true,
    order: 6,
  },
  attachments: {
    type: QuestionType.FILE_UPLOAD,
    label: 'forms_personal_ids:common.inputs.documentScan.label',
    isMultiple: true,
    order: 8,
  },
};

export type PersonalIdentifierDetailsHelp = {
  number: QuestionType.TEXT;
  dateOfIssue: QuestionType.DATE_TIME;
  endDate: QuestionType.DATE_TIME;
  gender: QuestionType.SELECT_FROM_LIST;
  birthDate: QuestionType.DATE_TIME;
  placeOfBirth: QuestionType.TEXT;
  issuedBy: QuestionType.TEXT;
  issuerCode: QuestionType.TEXT;
  basisForIssuingId: QuestionType.PICK_ARRAY_OF_DTO;
  other: QuestionType.TEXT;
  idStatus: QuestionType.SELECT_FROM_LIST;
  comments: QuestionType.TEXT_MULTILINE;
  storageLocation: QuestionType.TEXT;
  otherStorageLocation: QuestionType.TEXT;
  storageComments: QuestionType.TEXT_MULTILINE;
};

export type PersonalIdentifierDetailsFormType = TDtoWrapper<
  PersonalIdentifierDetails,
  PersonalIdentifierDetailsHelp
>;

export const commonDetailsFields: PersonalIdentifierDetailsFormType = {
  number: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.idNumber.label',
    order: 1,
  },
  dateOfIssue: {
    type: QuestionType.DATE_TIME,
    label: 'forms_personal_ids:common.inputs.dateOfIssue.label',
    order: 2,
  },
  endDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_personal_ids:common.inputs.endDate.label',
    order: 3,
  },
  gender: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:common.inputs.gender.label',
    items: GenderList,
    order: 4,
  },
  birthDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_personal_ids:common.inputs.birthDate.label',
    order: 5,
  },
  placeOfBirth: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.placeOfBirth.label',
    capitalized: true,
    order: 6,
  },
  issuedBy: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.issuedBy.label',
    capitalized: true,
    order: 7,
  },
  issuerCode: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.issuerCode.label',
    capitalized: true,
    order: 8,
  },
  other: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.other.label',
    capitalized: true,
    order: 9,
  },
  basisForIssuingId: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_personal_ids:common.inputs.basisForIssuingId.label',
    items: [CollectionName.PRIVATE_DOCUMENTS, CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS],
    mode: 'multiple',
    order: 10,
  },
  idStatus: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:common.inputs.idStatus.label',
    items: IdStatusList,
    order: 11,
  },
  comments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_personal_ids:common.inputs.comments.label',
    order: 12,
  },
  storageLocation: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:common.inputs.storageLocation.label',
    items: StorageLocationList,
    order: 13,
  },
  otherStorageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:common.inputs.otherStorageLocation.label',
    disableIf: {
      storageLocation: {
        value: StorageLocationType.OTHER,
      },
    },
    order: 14,
  },
  storageComments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_personal_ids:common.inputs.storageComments.label',
    order: 15,
    colSpan: 2,
  },
};

export type LegalizedCopyDto = {
  legalizationType?: LegalizationType;
  otherLegalizationType?: string;
  languageOfCopy?: string;
  numberOfCopies?: number;
  copyStorageLocation?: StorageLocationType;
  otherCopyStorageLocation?: string;
  copyStorageComments?: string;
  // copyScan?: string[];
};

type LegalizedCopy = {
  legalizedCopy?: LegalizedCopyDto[];
};

type LegalizedCopyHelpPart = Omit<BaseDto, 'id'> & {
  legalizedCopy?: QuestionType.LEGALIZED_COPIES;
};

export type LegalizedCopiesPartType = TDtoWrapper<LegalizedCopy, LegalizedCopyHelpPart>;

export const commonLegalizedCopiesFields: LegalizedCopiesPartType = {
  legalizedCopy: {
    label: 'Legalized copies',
    type: QuestionType.LEGALIZED_COPIES,
  },
};
